/**
 * @module StallControl
 * @purpose Stall/unstall affordance for a collection request -- offers "mark as stuck with client" while sent, shows the stall reason and a resume action while stalled (PRP §2 C6).
 * @depends useCollectionRequestMutations
 * @dependedOnBy ExpandedRequestDetail
 *
 * @rules
 * - State: local dialog open/reason draft state; mutation loading/error via useCollectionRequestMutations
 * - Errors: ApiError message surfaced inline; client-side non-empty reason guard before submit (server also 400s on empty reason)
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PauseCircle, PlayCircle } from 'lucide-react'
import { useCollectionRequestMutations } from '../../../hooks'
import type { PackageStatus } from '../../../types/collection-requests'

interface StallControlProps {
  caseId: string
  requestId: string
  packageStatus: PackageStatus
  stallReason: string | null
  onChanged: () => void
}

const FIELD_CLASS =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200'

interface StalledBannerProps {
  stallReason: string | null
  updating: boolean
  error: string | null
  onUnstall: () => void
}

function StalledBanner({ stallReason, updating, error, onUnstall }: StalledBannerProps): React.ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="space-y-2 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900/50 dark:bg-red-950/30">
      <p className="text-xs text-red-700 dark:text-red-400">
        <span className="font-medium">{t('collections.stalledReasonPrefix')}</span> {stallReason}
      </p>
      {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
      <button
        onClick={onUnstall}
        disabled={updating}
        className="items-centre inline-flex gap-1.5 rounded-lg border border-red-300 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/40"
      >
        <PlayCircle className="h-3.5 w-3.5" />
        {updating ? t('collections.unstalling') : t('collections.unstallButtonLabel')}
      </button>
    </div>
  )
}

interface StallDialogProps {
  reason: string
  updating: boolean
  error: string | null
  onReasonChange: (value: string) => void
  onSubmit: () => void
  onCancel: () => void
}

function StallDialog({ reason, updating, error, onReasonChange, onSubmit, onCancel }: StallDialogProps): React.ReactElement {
  const { t } = useTranslation('cases')
  const canSubmit = reason.trim().length > 0 && !updating
  return (
    <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/50">
      <div>
        <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
          {t('collections.stallDialogTitle')}
        </p>
        <p className="text-xs text-slate-500 dark:text-slate-400">{t('collections.stallDialogHint')}</p>
      </div>
      <label className="block space-y-1">
        <span className="text-xs font-medium text-slate-600 dark:text-slate-400">
          {t('collections.stallReasonLabel')}
        </span>
        <textarea
          value={reason}
          onChange={(event) => {
            onReasonChange(event.target.value)
          }}
          placeholder={t('collections.stallReasonPlaceholder')}
          className={FIELD_CLASS}
          rows={2}
        />
      </label>
      {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
      <div className="flex justify-end gap-2">
        <button
          onClick={onCancel}
          className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800"
        >
          {t('collections.stallCancel')}
        </button>
        <button
          onClick={onSubmit}
          disabled={!canSubmit}
          className="rounded-lg bg-[var(--accent)] px-3 py-1.5 text-xs font-medium text-white transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {updating ? t('collections.stallSubmitting') : t('collections.stallSubmit')}
        </button>
      </div>
    </div>
  )
}

export function StallControl({
  caseId,
  requestId,
  packageStatus,
  stallReason,
  onChanged,
}: StallControlProps): React.ReactElement | null {
  const { t } = useTranslation('cases')
  const { stall, unstall, updating, error } = useCollectionRequestMutations()
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')

  if (packageStatus !== 'sent' && packageStatus !== 'stalled') return null

  const handleUnstall = (): void => {
    void (async () => {
      const result = await unstall(caseId, requestId)
      if (result) onChanged()
    })()
  }

  const handleStall = (): void => {
    void (async () => {
      const result = await stall(caseId, requestId, { reason: reason.trim() })
      if (result) {
        setOpen(false)
        setReason('')
        onChanged()
      }
    })()
  }

  if (packageStatus === 'stalled') {
    return (
      <StalledBanner
        stallReason={stallReason}
        updating={updating}
        error={error}
        onUnstall={handleUnstall}
      />
    )
  }

  if (!open) {
    return (
      <button
        onClick={() => {
          setReason('')
          setOpen(true)
        }}
        className="items-centre inline-flex gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800"
      >
        <PauseCircle className="h-3.5 w-3.5" />
        {t('collections.stallButtonLabel')}
      </button>
    )
  }

  return (
    <StallDialog
      reason={reason}
      updating={updating}
      error={error}
      onReasonChange={setReason}
      onSubmit={handleStall}
      onCancel={() => {
        setOpen(false)
      }}
    />
  )
}
